<template>
  <div>
    <!-- ===================== HEADER ===================== -->
    <v-row class="pa-0">
      <v-col cols="12" class="pt-2">
        <v-card class="card--header-compact" dark>
          <div class="header-compact">
            <!-- FILA SUPERIOR -->
            <div class="hc-top">
              <div class="hc-top-left">
                <div class="hc-title-wrap">
                  <v-icon size="18" class="mr-2">mdi-account-multiple-plus</v-icon>
                  <span class="hc-title">Referidos</span>
                </div>

                <div class="hc-code-row">
                  <span class="hc-code-label">Código de referido:</span>
                  <span class="code-badge">{{ codreferido }}</span>
                </div>
              </div>

              <div class="hc-top-right">
                <span class="hc-switch-label mr-2">Todos</span>
                <v-switch
                  v-model="mostrarTodos"
                  inset
                  hide-details
                  class="switch-compact"
                  color="primary"
                />
              </div>
            </div>

            <!-- FILA INFERIOR -->
            <div v-if="referralUrl" class="hc-bottom">
              <div class="hc-link">
                <v-icon size="14" class="mr-1 hc-link-icon">mdi-link-variant</v-icon>
                <a :href="referralUrl" target="_blank" rel="noopener noreferrer">
                  {{ referralUrl }}
                </a>
              </div>

              <div class="hc-actions">
                <v-btn
                  x-small
                  color="primary"
                  class="ref-btn-mini"
                  @click="copyReferralLink"
                >
                  <v-icon left small>{{ copied ? "mdi-check" : "mdi-content-copy" }}</v-icon>
                  {{ copied ? "Copiado" : "" }}
                </v-btn>

                <v-btn
                  x-small
                  outlined
                  color="white"
                  class="ref-btn-mini"
                  @click="shareReferralLink"
                >
                  <v-icon left small>mdi-share-variant</v-icon>
                  Compartir
                </v-btn>
              </div>
            </div>
          </div>
        </v-card>
      </v-col>

      <!-- ===================== STAT CARDS ===================== -->
      <!-- 1) Ingresos - Balance (arriba 20%, abajo 5%) -->
      <v-col cols="12" sm="6" md="3" class="pt-3">
        <v-card class="mx-auto card--stat card--gradient-slate" dark elevation="2">
          <v-list-item class="py-3">
            <v-list-item-content>
              <div class="stat-title">Ingresos − Balance</div>
              <div class="stack">
                <div class="stack-row">
                  <span class="stack-badge badge-20">20%</span>
                  <span class="stack-value">
                    {{ formatUND(ingBal20) }} <span class="stat-unit">UND</span>
                  </span>
                </div>
                <div class="stack-row">
                  <span class="stack-badge badge-5">5%</span>
                  <span class="stack-value">
                    {{ formatUND(ingBal5) }} <span class="stat-unit">UND</span>
                  </span>
                </div>
              </div>
            </v-list-item-content>
            <v-list-item-avatar color="white" size="34" class="elev">
              <v-icon color="#0f172a" size="18">UND</v-icon>
            </v-list-item-avatar>
          </v-list-item>
        </v-card>
      </v-col>

      <!-- 2) Retiros + Bonos (arriba 20%, abajo 5%) -->
      <v-col cols="12" sm="6" md="3" class="pt-3">
        <v-card class="mx-auto card--stat card--gradient-yellow" dark elevation="2">
          <v-list-item class="py-3">
            <v-list-item-content>
              <div class="stat-title">Retiros + Bonos</div>
              <div class="stack">
                <div class="stack-row">
                  <span class="stack-badge badge-20">20%</span>
                  <span class="stack-value">
                    {{ formatUND(retirosBonos20) }} <span class="stat-unit">UND</span>
                  </span>
                </div>
                <div class="stack-row">
                  <span class="stack-badge badge-5">5%</span>
                  <span class="stack-value">
                    {{ formatUND(retirosBonos5) }} <span class="stat-unit">UND</span>
                  </span>
                </div>
              </div>
            </v-list-item-content>
            <v-list-item-avatar color="white" size="34" class="elev">
              <v-icon color="#0f172a" size="18">UND</v-icon>
            </v-list-item-avatar>
          </v-list-item>
        </v-card>
      </v-col>

      <!-- 3) Diferencia por tier (arriba 20%, abajo 5%) -->
      <v-col cols="12" sm="6" md="3" class="pt-3">
        <v-card class="mx-auto card--stat card--gradient-blue" dark elevation="2">
          <v-list-item class="py-3">
            <v-list-item-content>
              <div class="stat-title">Diferencia</div>
              <div class="stack">
                <div class="stack-row">
                  <span class="stack-badge badge-20">20%</span>
                  <span class="stack-value">
                    {{ formatUND(diferencia20) }} <span class="stat-unit">UND</span>
                  </span>
                </div>
                <div class="stack-row">
                  <span class="stack-badge badge-5">5%</span>
                  <span class="stack-value">
                    {{ formatUND(diferencia5) }} <span class="stat-unit">UND</span>
                  </span>
                </div>
              </div>
            </v-list-item-content>
            <v-list-item-avatar color="white" size="34" class="elev">
              <v-icon color="#0f172a" size="18">UND</v-icon>
            </v-list-item-avatar>
          </v-list-item>
        </v-card>
      </v-col>

      <!-- 4) Recompensa Total por tier (arriba 20%, abajo 5%) + TOTAL -->
      <v-col cols="12" sm="6" md="3" class="pt-3">
        <v-card class="mx-auto card--stat card--gradient-mint" dark elevation="2">
          <v-list-item class="py-3">
            <v-list-item-content>
              <div class="stat-title">Recompensa Total</div>
              <div class="stack">
                <div class="stack-row">
                  <span class="stack-badge badge-20">20%</span>
                  <span class="stack-value">
                    {{ formatUND(totalReward20) }} <span class="stat-unit">UND</span>
                  </span>
                </div>
                <div class="stack-row">
                  <span class="stack-badge badge-5">5%</span>
                  <span class="stack-value">
                    {{ formatUND(totalReward5) }} <span class="stat-unit">UND</span>
                  </span>
                </div>
                <v-divider class="my-1 divider-light"></v-divider>
                <div class="stack-row total-general">
                  <div class="stack-left">
                    <v-icon color="#facc15" size="16" class="mr-1">mdi-trophy-outline</v-icon>
                    <span class="stack-badge badge-total">TOTAL</span>
                  </div>
                  <span class="stack-value">
                    {{ formatUND(totalRewardCliente) }} <span class="stat-unit">UND</span>
                  </span>
                </div>
              </div>
            </v-list-item-content>
            <v-list-item-avatar color="white" size="34" class="elev">
              <v-icon color="#0f172a" size="18">UND</v-icon>
            </v-list-item-avatar>
          </v-list-item>
        </v-card>
      </v-col>
    </v-row>

    <!-- ===================== TABLE ===================== -->
    <v-card class="mt-3" :color="colors.sidebar" elevation="2">
      <!-- Toolbar -->
      <div class="toolbar-surface">
        <div class="toolbar-left">
          <span class="toolbar-title">Mis referidos</span>
        </div>
        <div class="toolbar-right">
          <filter-table
            v-model="search"
            :dateTo="dateTo"
            :dateFrom="dateFrom"
            @ok="updateTable"
            class="filter-compact"
          />
        </div>
      </div>

      <v-data-table
        class="table-elev zebra compact-table"
        :loading="loading"
        :height="$store.state.windowSize.x < 960 ? 'calc(100vh - 460px)' : 'calc(100vh - 330px)'"
        :headers="headers"
        :items="filteredRewards"
        show-expand
        :expanded.sync="expandedPre"
        :items-per-page="ITEMS_PER_PAGE"
        item-key="id_usuario"
        :search="search"
        must-sort
        :disable-pagination="false"
        :item-class="rowClass"
        dense
      >
        <!-- USUARIO -->
        <template #item.nombre="{ item }">
          <span class="name-text">{{ item.nombre }}</span>
        </template>

        <!-- INICIO: fecha + meta pequeña (días + badge 20%/5%) -->
        <template #item.inicio="{ item }">
          <div class="inicio-wrap">
            <div class="inicio-fecha">{{ formatFechaSoloDia(item.inicio) }}</div>
            <div class="inicio-meta">
              <span class="meta-small">{{ item.diasTranscurridos }} días</span>
              <span
                class="badge-rate"
                :class="item.rate === 0.20 ? 'rate-20' : 'rate-5'"
                :title="item.rate === 0.20 ? 'Recompensa 20%' : 'Recompensa 5%'"
              >
                {{ (item.rate * 100).toFixed(0) }}%
              </span>
            </div>
          </div>
        </template>

        <!-- Números -->
        <template #item.balance="{ item }"><span class="num">{{ formatUND(item.balance) }}</span></template>
        <template #item.bonos="{ item }"><span class="num">{{ formatUND(item.bonos) }}</span></template>
        <template #item.vendidos="{ item }"><span class="num">{{ formatUND(item.vendidos) }}</span></template>
        <template #item.pagados="{ item }"><span class="num">{{ formatUND(item.pagados) }}</span></template>
        <template #item.resta="{ item }">
          <span class="num" :class="{ neg: Number(item.resta) < 0, zero: Number(item.resta) === 0 }">
            {{ formatUND(item.resta) }}
          </span>
        </template>

        <template #footer>
          <v-divider></v-divider>
          <div class="footer-summary">
            <div class="footer-item">Total Ingresos: <b>{{ formatUND(totalv) }}</b></div>
            <div class="footer-item">Retiros + Bonos: <b>{{ formatUND(totalp) }}</b></div>
            <div class="footer-item">Registros: <b>{{ filteredRewards.length }}</b></div>
          </div>
        </template>
      </v-data-table>

      <v-alert v-if="errorMessage" type="error" color="error" dense border="left" class="ma-4">
        {{ errorMessage }}
      </v-alert>
    </v-card>
  </div>
</template>

<script>
import axios from "axios";
import { mapActions, mapMutations } from "vuex";
import date from "../../date.js";
import parseDate from "../../parseDate.js";
import filterTable from "../../components/filterTable.vue";

export default {
  name: "Referidos",
  components: { filterTable },
  data() {
    return {
      loading: false,
      errorMessage: "",
      expandedPre: [],
      search: "",
      rewards: [],
      mostrarTodos: false,
      copied: false,
      copyTimer: null,
      dateFrom: date.primerDia(),
      dateTo: date.today(),
      totalv: 0, // backend: suma (vendidos - balance)
      totalp: 0, // backend: suma (pagados + bonos)
      headers: [
        { text: "", value: "data-table-expand", sortable: false, width: 24 },
        { text: "USUARIO", value: "nombre" },
        { text: "INICIO", value: "inicio" },
        { text: "BALANCE", value: "balance", align: "end" },
        { text: "BONOS", value: "bonos", align: "end" },
        { text: "INGRESOS", value: "vendidos", align: "end" },
        { text: "RETIROS", value: "pagados", align: "end" },
        { text: "DIFERENCIA", value: "resta", align: "end" },
      ],
      cancelSource: null,
      ITEMS_PER_PAGE: 10,
    };
  },
  computed: {
    idmoneda() { return this.$store.state.idmoneda; },
    colors() { return this.$store.state.colors; },
    codreferido() { return this.$store.state.codreferido || ""; },

    // === Lista visible (lo que se ve en la tabla) ===
    filteredRewards() {
      return this.mostrarTodos ? this.rewards : this.rewards.filter(r => Number(r.resta) !== 0);
    },

    referralUrl() {
      const baseUrl = window.location.origin;
      return this.codreferido
        ? `${baseUrl}/#/?ref=${encodeURIComponent(this.codreferido)}`
        : "";
    },

    // === Segmentación visible por tier (20% / 5%) ===
    items20() { return this.filteredRewards.filter(r => r.rate === 0.20); },
    items5() { return this.filteredRewards.filter(r => r.rate === 0.05); },

    // 1) Ingresos - Balance por tier (desde tabla)
    ingBal20() { return this.items20.reduce((acc, r) => acc + ((+r.vendidos || 0) - (+r.balance || 0)), 0); },
    ingBal5() { return this.items5.reduce((acc, r) => acc + ((+r.vendidos || 0) - (+r.balance || 0)), 0); },

    // 2) Retiros + Bonos por tier (desde tabla)
    retirosBonos20() { return this.items20.reduce((acc, r) => acc + ((+r.pagados || 0) + (+r.bonos || 0)), 0); },
    retirosBonos5() { return this.items5.reduce((acc, r) => acc + ((+r.pagados || 0) + (+r.bonos || 0)), 0); },

    // 3) Diferencia por tier (sum(resta) desde tabla)
    diferencia20() { return this.items20.reduce((acc, r) => acc + (+r.resta || 0), 0); },
    diferencia5() { return this.items5.reduce((acc, r) => acc + (+r.resta || 0), 0); },

    // 4) Recompensas por tier aplicando % a la DIFERENCIA TOTAL (si es > 0)
    totalReward20() {
      const base = Math.max(this.diferencia20, 0);
      return base * 0.20;
    },
    totalReward5() {
      const base = Math.max(this.diferencia5, 0);
      return base * 0.05;
    },

    // 🔹 Suma total que recibirá el cliente (20% + 5%)
    totalRewardCliente() {
      return this.totalReward20 + this.totalReward5;
    },
  },
  watch: {
    idmoneda() { this.getRewards(); },
  },
  created() {
    this.parseDate();
    this.dateFrom = date.primerDia();
    this.dateTo = date.today();
  },
  mounted() {
    const code = sessionStorage.getItem("codreferido") || "";
    this.SET_COD_REFERIDO(code);
    this.getRewards();
  },
  beforeDestroy() {
    if (this.cancelSource) {
      this.cancelSource.cancel("Componente destruido");
      this.cancelSource = null;
    }

    if (this.copyTimer) {
      clearTimeout(this.copyTimer);
      this.copyTimer = null;
    }
  },
  methods: {
    ...mapActions(["getSaldo"]),
    ...mapMutations({
      SET_COD_REFERIDO: "SET_COD_REFERIDO",
      SET_SNACK: "SET_SNACK",
      SET_MSG: "SET_MSG",
    }),
    parseDate,

    updateTable(newFrom, newTo) {
      this.dateFrom = newFrom;
      this.dateTo = newTo;
      this.getRewards();
    },

    formatUND(value) {
      const n = Number(value);
      return Number.isFinite(n) ? n.toFixed(2) : "0.00";
    },

    // Solo fecha DD/MM/AAAA (para columna INICIO)
    formatFechaSoloDia(v) {
      if (!v) return "-";
      const d = new Date(v.replace(" ", "T"));
      if (Number.isNaN(d.getTime())) return v;
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yyyy = d.getFullYear();
      return `${dd}/${mm}/${yyyy}`;
    },

    async copyReferralLink() {
      try {
        if (!this.referralUrl) return;

        await navigator.clipboard.writeText(this.referralUrl);

        this.copied = true;
        if (this.copyTimer) clearTimeout(this.copyTimer);
        this.copyTimer = setTimeout(() => {
          this.copied = false;
          this.copyTimer = null;
        }, 1500);

        this.SET_MSG("Link de referido copiado");
        this.SET_SNACK(true);
      } catch (e) {
        this.SET_MSG("No se pudo copiar el link");
        this.SET_SNACK(true);
      }
    },

    async shareReferralLink() {
      try {
        if (!this.referralUrl) return;

        if (navigator.share) {
          await navigator.share({
            title: "Mi link de referido",
            text: "Únete con mi link de referido",
            url: this.referralUrl,
          });
          return;
        }

        await navigator.clipboard.writeText(this.referralUrl);
        this.SET_MSG("Tu dispositivo no permite compartir directamente. Link copiado.");
        this.SET_SNACK(true);
      } catch (e) {
        this.SET_MSG("No se pudo compartir el link");
        this.SET_SNACK(true);
      }
    },

    rowClass(item) {
      const r = Number(item.resta || 0);
      return r < 0 ? "row--negativa" : "";
    },

    async getRewards() {
      if (!this.codreferido) return;
      if (this.cancelSource) this.cancelSource.cancel("Nueva solicitud iniciada");

      this.cancelSource = axios.CancelToken.source();
      this.loading = true;
      this.errorMessage = "";

      try {
        const url = `reporte_referidos/${this.dateFrom}/${this.dateTo}/${this.codreferido}`;
        const { data } = await axios.get(url, { cancelToken: this.cancelSource.token });

        const num = (v) => (v == null || v === "" ? 0 : Number(v));
        const fechaFin = new Date(this.dateTo);

        const mapRow = (row) => {
          const fechaInicio = new Date(row.inicio?.replace(" ", "T"));
          const diffMs = fechaFin - fechaInicio;
          const dias = Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
          const rate = dias <= 60 ? 0.20 : 0.05; // regla por antigüedad

          return {
            nombre: row.nombre,
            inicio: row.inicio || null,
            diasTranscurridos: dias,
            id_usuario: String(row.id_usuario),
            balance: num(row.balance),
            bonos: num(row.bonos),
            vendidos: num(row.vendidos),
            pagados: num(row.pagados),
            resta: num(row.resta),
            rate, // 0.20 o 0.05
          };
        };

        this.rewards = Array.isArray(data.premiados)
          ? data.premiados.map(mapRow).sort((a, b) => a.diasTranscurridos - b.diasTranscurridos)
          : [];

        // Totales globales del backend (para el footer)
        this.totalv = num(data.totalv); // suma de (vendidos - balance)
        this.totalp = num(data.totalp); // suma de (pagados + bonos)
      } catch (err) {
        if (!axios.isCancel(err)) {
          this.errorMessage = "No se pudieron cargar los datos de referidos. Intenta nuevamente.";
          this.SET_MSG(this.errorMessage);
          this.SET_SNACK(true);
          // eslint-disable-next-line no-console
          console.error("[getRewards] error:", err);
        }
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
/* ====== Header ====== */
.card--header-elev {
  background: linear-gradient(180deg, #0b1b30 0%, #0e2037 100%);
  border-radius: 12px;
}

.header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  gap: 12px;
  flex-wrap: wrap;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.header-right {
  display: flex;
  align-items: center;
}

/* Switch pequeño */
.small-switch >>> .v-input--selection-controls__input {
  transform: scale(0.82);
}

.small-switch >>> .v-label {
  font-size: 12px !important;
  color: #e5e7eb !important;
  margin-top: 1px;
}

.switch-contrast >>> .v-input--selection-controls__ripple {
  color: #60a5fa !important;
}

.code-badge {
  padding: 3px 10px;
  font-size: 12px;
  background: linear-gradient(180deg, rgba(255,255,255,0.18), rgba(255,255,255,0.08));
  border: 1px solid rgba(255,255,255,0.25);
  border-radius: 9px;
  font-weight: 800;
  letter-spacing: 0.4px;
  color: #fff;
  line-height: 1.2;
}

/* ====== Stat Cards ====== */
.card--stat {
  border-radius: 14px;
}

.stat-title {
  font-size: 13px;
  font-weight: 700;
  letter-spacing: .3px;
  opacity: .95;
  margin-bottom: 4px;
}

.stat-value {
  font-size: 22px;
  font-weight: 700;
  letter-spacing: 0.2px;
}

.stat-unit {
  font-size: 12px;
  opacity: 0.9;
  margin-left: 4px;
  font-weight: 600;
}

.stat-caption {
  font-size: 12px;
  opacity: 0.85;
  margin-top: 2px;
}

.stack {
  display: grid;
  gap: 6px;
}

.stack-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.stack-value {
  font-size: 16px;
  font-weight: 800;
  letter-spacing: .2px;
}

.badge-20,
.badge-5 {
  font-size: 11px;
  font-weight: 800;
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid transparent;
  line-height: 1;
}

.badge-20 {
  background: #d1fae5;
  color: #065f46;
  border-color: #a7f3d0;
}

.badge-5 {
  background: #e0e7ff;
  color: #3730a3;
  border-color: #c7d2fe;
}

.card--gradient-slate {
  background-image: linear-gradient(135deg, #0f172a, #1f2a44);
}

.card--gradient-yellow {
  background-image: linear-gradient(135deg, #ffb20a, #ffd36b);
}

.card--gradient-blue {
  background-image: linear-gradient(135deg, #2e6fd6, #02a7e7);
}

.card--gradient-mint {
  background-image: linear-gradient(135deg, #1f9d5f, #46d392);
}

.elev {
  box-shadow: 0 2px 6px rgba(0, 0, 0, .18);
  border: 1px solid rgba(0, 0, 0, .06);
}

/* ====== Toolbar ====== */
.toolbar-surface {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f7f7f9;
  border-bottom: 1px solid #e7e7ee;
  padding: 10px 14px;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  flex-wrap: wrap;
  gap: 10px;
}

.toolbar-title {
  color: #111827;
  font-weight: 700;
  font-size: 15px;
  text-transform: uppercase;
  letter-spacing: .6px;
}

.filter-compact {
  margin-left: 0;
}

/* ====== Table ====== */
.table-elev >>> .v-data-table-header {
  background: linear-gradient(180deg, #0b1220 0%, #14213a 100%);
}

.table-elev >>> th {
  color: #e5e7eb !important;
  font-weight: 700 !important;
  font-size: 11px !important;
  letter-spacing: .4px;
  text-transform: uppercase;
}

/* tamaño compacto */
.compact-table >>> .v-data-table__wrapper td {
  padding: 8px 12px !important;
  font-size: 12.5px;
  color: #0f172a;
  background: #ffffff;
}

.table-elev.zebra >>> .v-data-table__wrapper tbody tr:nth-child(odd) td {
  background: #fafbff;
}

.table-elev >>> tbody tr:hover td {
  background: #eef2ff !important;
}

/* Columna INICIO compacta */
.inicio-wrap {
  display: flex;
  flex-direction: column;
  line-height: 1.15;
}

.inicio-fecha {
  font-weight: 700;
  font-size: 12.5px;
  color: #0f172a;
}

.inicio-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 3px;
}

.meta-small {
  font-size: 11.5px;
  color: #6b7280;
}

/* Badge de tasa muy compacto */
.badge-rate {
  font-size: 10.5px;
  font-weight: 800;
  padding: 2px 6px;
  border-radius: 999px;
  border: 1px solid transparent;
  line-height: 1;
}

.rate-20 {
  background: #d1fae5;
  color: #065f46;
  border-color: #a7f3d0;
}

.rate-5 {
  background: #e0e7ff;
  color: #3730a3;
  border-color: #c7d2fe;
}

.name-text {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* números */
.num {
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum";
  text-align: right;
  display: inline-block;
  min-width: 64px;
}

.num.neg {
  color: #b91c1c;
  font-weight: 700;
}

.num.zero {
  color: #6b7280;
}

.row--negativa td {
  box-shadow: inset 3px 0 0 0 #ef4444;
}

/* Divider claro en stat card mint */
.divider-light {
  border-color: rgba(255, 255, 255, 0.3) !important;
  margin: 6px 0;
}

.badge-total {
  background: #f3f4f6;
  color: #111827;
  font-weight: 800;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  border: 1px solid #d1d5db;
}

.total-general {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.total-general .stack-left {
  display: flex;
  align-items: center;
}

.ref-link-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.ref-link-text {
  padding: 4px 10px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 8px;
  font-size: 12px;
  color: #e5e7eb;
  max-width: 100%;
  word-break: break-all;
}

.ref-action-btn {
  text-transform: none;
}

.card--header-compact {
  background: linear-gradient(90deg, #0b1b30, #0f223b);
  border-radius: 12px;
  padding: 10px 14px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
}

.header-compact {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.hc-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.hc-top-left {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.hc-title-wrap {
  display: flex;
  align-items: center;
}

.hc-title {
  font-weight: 700;
  font-size: 13px;
  color: #e5e7eb;
}

.hc-code-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.hc-code-label {
  font-size: 11px;
  color: #94a3b8;
  font-weight: 600;
}

.hc-top-right {
  display: flex;
  align-items: center;
}

.hc-switch-label {
  font-size: 11px;
  color: #94a3b8;
}

.hc-bottom {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  flex-wrap: wrap;
}

.hc-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #94a3b8;
  background: rgba(255,255,255,0.08);
  border: 1px solid rgba(255,255,255,0.12);
  padding: 5px 9px;
  border-radius: 8px;
  min-width: 0;
  max-width: 430px;
  overflow: hidden;
}

.hc-link-icon {
  flex: 0 0 auto;
  color: #93c5fd;
}

.hc-link a {
  color: #93c5fd;
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
  max-width: 100%;
}

.hc-link a:hover {
  text-decoration: underline;
}

.hc-actions {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.ref-btn-mini {
  text-transform: none;
  font-weight: 600;
  border-radius: 8px;
  padding: 0 10px;
  min-width: auto !important;
  letter-spacing: 0;
}

.switch-compact >>> .v-input--selection-controls__input {
  transform: scale(0.75);
}

/* ====== Responsive ====== */
@media (max-width: 960px) {
  .header-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }

  .small-switch >>> .v-label {
    font-size: 13px !important;
  }

  .toolbar-surface {
    padding: 10px 12px;
  }

  .compact-table >>> .v-data-table__wrapper td {
    padding: 8px 10px !important;
    font-size: 12px;
  }

  .hc-link {
    max-width: 100%;
  }
}

@media (max-width: 768px) {
  .card--header-compact {
    padding: 10px 12px;
  }

  .hc-top {
    align-items: flex-start;
  }

  .hc-top-right {
    margin-top: 2px;
  }

  .hc-bottom {
    align-items: stretch;
    gap: 8px;
  }

  .hc-link {
    width: 100%;
    max-width: 100%;
    padding: 6px 9px;
  }

  .hc-link a {
    width: 100%;
  }

  .hc-actions {
    width: 100%;
    display: flex;
    gap: 8px;
  }

  .hc-actions .v-btn {
    flex: 1 1 calc(50% - 4px);
  }

  .ref-btn-mini {
    justify-content: center;
  }
}
</style>